import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { toast } from 'sonner';
import { useWalletStore } from '../stores/useWalletStore';
import { getClientAuctionDetail } from '../data/mockAdapter';

// ─── Types ──────────────────────────────────────────────────────────────────────

type AuctionStatus = 'active' | 'ending_soon' | 'settled';

const STATUS_CONFIG: Record<AuctionStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
  ending_soon: { label: 'Ending Soon', className: 'bg-amber-100 text-amber-700' },
  settled: { label: 'Settled', className: 'bg-gray-100 text-gray-600' },
};

const WALLET_BALANCE = 100000;

// ─── Page ───────────────────────────────────────────────────────────────────────

export function DdfAuctionDetailPage() {
  const { auctionId } = useParams<{ auctionId: string }>();
  const { isConnected } = useWalletStore();

  const auction = getClientAuctionDetail(auctionId ?? 'DDF-2041');
  const statusCfg = STATUS_CONFIG[auction.status];
  const isBiddable = auction.status !== 'settled';
  const reserveDeltaPct = auction.currentBid > 0
    ? ((auction.currentBid - auction.reservePrice) / auction.reservePrice * 100)
    : null;
  const minBid = auction.currentBid > 0 ? Math.ceil(auction.currentBid * 1.01) : auction.reservePrice;

  const [bidAmount, setBidAmount] = useState('');
  const [bidding, setBidding] = useState(false);
  const [buying, setBuying] = useState(false);
  const [userBids, setUserBids] = useState<{ amount: number; time: string; leading: boolean }[]>([]);

  const kycPassed = true;
  const claimsPassed = true;
  const bidderRegistryPassed = !auction.bidderRegistryRequired;
  const balanceOk = WALLET_BALANCE >= minBid;

  function handleBuyAtReserve() {
    setBuying(true);
    setTimeout(() => {
      setBuying(false);
      toast.success(`Purchased collateral at reserve price of $${auction.reservePrice.toLocaleString()}`);
    }, 2000);
  }

  function handleBid() {
    const amt = parseFloat(bidAmount);
    if (isNaN(amt) || amt < minBid) {
      toast.error(`Minimum bid is $${minBid.toLocaleString()}`);
      return;
    }
    setBidding(true);
    setTimeout(() => {
      setBidding(false);
      const now = new Date();
      const ts = `${now.toLocaleDateString('en-GB')}, ${now.toLocaleTimeString('en-GB')}`;
      setUserBids((prev) => [
        ...prev.map((b) => ({ ...b, leading: false })),
        { amount: amt, time: ts, leading: true },
      ]);
      setBidAmount('');
      toast.success(`Bid of $${amt.toLocaleString()} placed successfully`);
    }, 2000);
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Back link + status */}
      <div className="flex items-center justify-between">
        <Link
          to="/liquidations?tab=ddf"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
          Back to Auctions
        </Link>
        <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusCfg.className}`}>
          {statusCfg.label}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Left column */}
        <div className="space-y-6">
          {/* Auction Details */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="flex items-center gap-2">
                <Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="h-5 w-5" />
                Auction Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-4">
                  <div>
                    <span className="text-xs text-muted-foreground">Collateral</span>
                    <p className="text-sm font-medium mt-0.5">{auction.tokenName}</p>
                    <p className="text-lg font-bold">{auction.collateralAmount} {auction.tokenSymbol}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Collateral Value</span>
                    <p className="text-lg font-bold">${auction.collateralValue.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Debt Amount</span>
                    <p className="text-lg font-bold">${auction.debtAmount.toLocaleString()}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="text-xs text-muted-foreground">Facility</span>
                    <p className="text-sm font-semibold mt-0.5">{auction.facility}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Reserve Price</span>
                    <p className="text-lg font-bold">${auction.reservePrice.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Time Remaining</span>
                    <p className="flex items-center gap-2 text-lg font-bold">
                      <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-4 w-4 text-muted-foreground" />
                      {auction.timeRemaining}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Reserve Price Buyout</span>
                    <p className="flex items-center gap-1.5 text-sm font-medium mt-0.5">
                      {auction.reservePriceBuyoutEnabled ? (
                        <><Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-4 w-4 text-green-600" /> Enabled</>
                      ) : (
                        <><Icon name="streamline:interface-delete-circle-button-delete-remove-add-circle-buttons" className="h-4 w-4 text-muted-foreground" /> Disabled</>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Settlement Conditions */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Settlement Conditions</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>&#8226; Winning bidder must claim collateral within 7 days</li>
                <li>&#8226; Settlement token: {auction.settlementToken}</li>
                <li>&#8226; Minimum bid increment: 1% of current bid</li>
                <li>&#8226; Auction extends by 10 minutes if bid placed in final 10 minutes</li>
                <li>&#8226; Margin account ownership (NFT) is transferred to the winning bidder upon settlement</li>
              </ul>
            </CardContent>
          </Card>

          {/* Current Bidding Status */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Current Bidding Status</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <span className="text-xs text-muted-foreground">Current Bid</span>
                  <p className="text-2xl font-bold mt-1">
                    {auction.currentBid > 0 ? `$${auction.currentBid.toLocaleString()}` : 'No bids'}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Total Bids</span>
                  <p className="text-2xl font-bold mt-1">{auction.bidCount}</p>
                </div>
              </div>
              {reserveDeltaPct !== null && (
                <p className={`mt-3 text-sm flex items-center gap-1 ${reserveDeltaPct >= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                  <Icon name="streamline:graph-bar-increase" className="h-3.5 w-3.5" />
                  {reserveDeltaPct >= 0
                    ? `${reserveDeltaPct.toFixed(1)}% above reserve price`
                    : `${Math.abs(reserveDeltaPct).toFixed(1)}% below reserve price`}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Auction Timeline */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Auction Timeline</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="space-y-4">
                {[...auction.timeline].reverse().map((entry, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`h-2.5 w-2.5 rounded-full flex-shrink-0 mt-1.5 ${
                        idx === 0 ? 'bg-primary' : 'bg-muted-foreground/40'
                      }`} />
                      {idx < auction.timeline.length - 1 && (
                        <div className="w-px flex-1 bg-border mt-1" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 pb-4">
                      <p className="text-sm font-medium">{entry.description}</p>
                      {entry.bidder && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          By <span className="font-mono">{entry.bidder}</span>
                          {entry.amount && <span className="ml-2 font-medium">${entry.amount.toLocaleString()}</span>}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground">{entry.timestamp}</span>
                        {entry.txHash && (
                          <span className="inline-flex items-center gap-1 text-xs text-primary font-mono">
                            {entry.txHash}
                            <Icon name="streamline:share-link" className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Eligibility Check */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Eligibility Check</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-3">
              <EligibilityRow label="KYC Verified" passed={kycPassed} />
              <EligibilityRow label="Required Claims" passed={claimsPassed} />
              <EligibilityRow
                label="Bidder Registry"
                passed={bidderRegistryPassed}
                warning={auction.bidderRegistryRequired ? 'Not in Bidder Registry' : undefined}
              />
              <EligibilityRow
                label="Wallet Balance"
                passed={isConnected && balanceOk}
                warning={!isConnected ? 'Wallet not connected' : !balanceOk ? 'Insufficient balance' : undefined}
              />
            </CardContent>
          </Card>

          {/* Compliant Holders Notice */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
            <Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <span className="font-medium">Compliant Holders Only</span>{" "}
              &mdash; Only compliant holders can participate in auction settlement,
              as determined by each facility&apos;s compliance module. Ensure
              all eligibility checks above pass before placing a bid.
            </p>
          </div>

          {/* Place Bid */}
          {isBiddable && (
            <Card>
              <CardHeader className="p-6">
                <CardTitle className="text-base">Place Bid</CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                    Bid Amount ({auction.settlementToken})
                  </label>
                  <input
                    type="number"
                    placeholder={`Min: $${minBid.toLocaleString()}`}
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    disabled={!isConnected || !isBiddable}
                  />
                  {isConnected && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Wallet Balance: ${WALLET_BALANCE.toLocaleString()}
                    </p>
                  )}
                </div>
                <Button
                  className="w-full"
                  disabled={!isConnected || !isBiddable || bidding || !bidAmount || parseFloat(bidAmount) < minBid}
                  onClick={handleBid}
                >
                  {bidding ? (
                    <><Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 mr-1.5 animate-spin" /> Placing Bid...</>
                  ) : (
                    'Review Bid'
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Buy at Reserve Price */}
          {isBiddable && auction.reservePriceBuyoutEnabled && auction.currentBid < auction.reservePrice && (
            <Card>
              <CardHeader className="p-6">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon name="streamline:shopping-cart-1" className="h-4 w-4" />
                  Buy at Reserve Price
                </CardTitle>
                <CardDescription>
                  Reserve Price: ${auction.reservePrice.toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-4">
                <div className="flex items-start gap-2 rounded-md bg-muted/50 p-3">
                  <Icon name="streamline:interface-alert-information-circle-information-frame-info-more-help-point-circle" className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">
                    Purchase collateral immediately at the reserve price. This option is configured on the credit facility&apos;s auction module.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={!isConnected || buying}
                  onClick={handleBuyAtReserve}
                >
                  {buying ? (
                    <><Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 mr-1.5 animate-spin" /> Processing...</>
                  ) : (
                    <><Icon name="streamline:shopping-cart-1" className="h-4 w-4 mr-1.5" /> Buy at Reserve Price</>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Your Bids */}
          {userBids.length > 0 && (
            <Card>
              <CardHeader className="p-6">
                <CardTitle className="text-base">Your Bids</CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-3">
                {userBids.map((bid, idx) => (
                  <div key={idx} className="flex items-center justify-between rounded-lg border border-border p-3">
                    <div>
                      <p className="text-sm font-bold">${bid.amount.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{bid.time}</p>
                    </div>
                    {bid.leading && (
                      <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                        leading
                      </span>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────────

function EligibilityRow({
  label,
  passed,
  warning,
}: {
  label: string;
  passed: boolean;
  warning?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {passed ? (
          <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-4 w-4 text-green-500" />
        ) : (
          <Icon name="streamline:interface-delete-circle-button-delete-remove-add-circle-buttons" className="h-4 w-4 text-red-500" />
        )}
        <span className="text-sm">{label}</span>
      </div>
      {warning && (
        <span className="text-xs text-red-600">{warning}</span>
      )}
    </div>
  );
}
